// eslint-disable-next-line
///  <reference path="./accounting-js/index.d.ts" />
import accounting from 'accounting-js';
import { isEqual } from 'lodash/fp';
import { NumberFormatting, SymbolPosition, SymbolSpacing } from '@mews-framework/globalization';
import { check } from '@mews-framework/utils';
import { multiplyNumbersAsDecimals } from '../decimal';
import {
    CurrencySettings,
    NumberServiceOptions,
    NumberSettings,
    NumberSettingsProperties,
    ValueSettingsProperties,
} from './types';

const NumberService = {
    format,
    unformat,
    formatPercent(number: number, localizationInfo: NumberFormatting, options: NumberServiceOptions = {}): string {
        const formatedPercentValue = format(multiplyNumbersAsDecimals(number, 100), localizationInfo, options);
        return options.showSymbol ?? true ? `${formatedPercentValue}%` : formatedPercentValue;
    },
    unformatPercent(number: string, localizationInfo: NumberFormatting): number {
        return unformat(number, localizationInfo) / 100;
    },
    formatCurrencyValue(
        currencyValue: number,
        localizationInfo: ValueSettingsProperties,
        options: NumberServiceOptions = {}
    ): string | never {
        check.number(currencyValue);

        const settings = getCurrencyValueSettings({
            ...localizationInfo,
            ...options,
        });
        return accounting.formatMoney(currencyValue, settings);
    },
};

function format(
    number: number,
    localizationInfo: NumberFormatting,
    options: NumberServiceOptions = {}
): string | never {
    check.number(number);
    const { precision = getPrecision(number), ...others } = options;
    const settings = getNumberSettings({ ...others, ...localizationInfo });

    return accounting.formatMoney(number, { ...settings, precision });
}

function unformat(number: string, localizationInfo: NumberFormatting): number | typeof NaN {
    const { decimalSeparator } = localizationInfo;
    const fallback = NaN;

    return accounting.unformat(number, decimalSeparator, fallback);
}

const getNumberSettings = ({
    decimalSeparator,
    groupSeparator,
    showPositiveSign = false,
}: NumberSettingsProperties): NumberSettings => ({
    decimal: decimalSeparator,
    format: {
        pos: showPositiveSign ? '+%v' : '%v',
        neg: '-%v',
        zero: '%v',
    },
    thousand: groupSeparator,
});

const getCurrencyValueSettings = ({
    symbol,
    symbolPosition,
    symbolSpacing,
    decimalSeparator,
    groupSeparator,
    precision,
    showPositiveSign = false,
    showSymbol = true,
}: ValueSettingsProperties): CurrencySettings => {
    const value = '%v';
    const formattedValue = showSymbol ? appendCurrencySymbol(value, symbolSpacing, symbolPosition) : value;
    const positiveFormattedValue = showPositiveSign ? `+${formattedValue}` : formattedValue;
    const negativeFormattedValue = `-${formattedValue}`;

    return {
        decimal: decimalSeparator,
        format: {
            pos: positiveFormattedValue,
            neg: negativeFormattedValue,
            zero: formattedValue,
        },
        precision,
        symbol,
        thousand: groupSeparator,
    };
};

const appendCurrencySymbol = (
    currencyFormat: string,
    symbolSpacing: SymbolSpacing,
    symbolPosition: SymbolPosition
): string => {
    const spacing = isEqual(symbolSpacing, SymbolSpacing.Single) ? ' ' : '';

    return isEqual(symbolPosition, SymbolPosition.Prefix)
        ? `%s${spacing}${currencyFormat}`
        : `${currencyFormat}${spacing}%s`;
};

const getPrecision = (number: number) => {
    if (!Number.isFinite(number)) {
        return 0;
    }

    let exp = 1;
    let precision = 0;
    while (Math.round(number * exp) / exp !== number) {
        exp *= 10;
        ++precision;
    }

    return precision;
};

export default NumberService;
